import { useMutation } from '@tanstack/react-query'
import React from 'react'
import { useNavigate } from 'react-router'
import invariant from 'tiny-invariant'

import { useHasMultipleReferralInvites } from '@/components/layouts/ReferralInviteLayout/hooks/useHasMultipleReferralInvites'
import { useRecordInteractionMutation } from '@/components/layouts/ReferralInviteLayout/hooks/useRecordInteractionMutation'
import { useReferralInvites } from '@/components/layouts/ReferralInviteLayout/hooks/useReferralInvites'
import { useReferralsTerm } from '@/components/layouts/ReferralInviteLayout/hooks/useReferralsTerm'
import ActionButton, { ActionButtonProps } from '@/components/misc/ActionButton'
import { useAuth } from '@/hooks/useAuth'
import useCurrentCompanyOrFail from '@/hooks/useCurrentCompanyOrFail'
import useNotyf from '@/hooks/useNotyf'
import { useParamOrFail } from '@/hooks/useParamOrFail'
import { useRefreshSessionMetadataMutation } from '@/hooks/useRefreshSessionMetadataMutation'
import { LONG_MESSAGE_DURATION, REFERRALS_TAB } from '@/lib/constants'
import { PARAMS } from '@/lib/params'
import { ROUTES } from '@/lib/routes'
import referralInviteService from '@/lib/services/referralInviteService'
import { cn } from '@/lib/utils'

type Props = Omit<ActionButtonProps, 'isProcessing'> & {
  companyIri: IRI
  children: React.ReactNode
}

const ClaimReferralButton: React.FC<Props> = ({
  companyIri,
  children,
  className,
  ...props
}) => {
  const currentCompany = useCurrentCompanyOrFail()
  const token = useParamOrFail(PARAMS.referralInvites.token)
  const notyf = useNotyf()
  const navigate = useNavigate()
  const referralsTerm = useReferralsTerm()
  const hasMultipleReferrals = useHasMultipleReferralInvites()
  const referralInvites = useReferralInvites()
  const hasSelectedDifferentCompany = currentCompany['@id'] !== companyIri
  const recordInteractionMutation = useRecordInteractionMutation()
  const refreshSessionMetadata = useRefreshSessionMetadataMutation()
  const { updateCompanyContext } = useAuth()

  const claimReferralMutation = useMutation({
    mutationFn: async () => {
      await recordInteractionMutation.mutateAsync()

      return referralInviteService.claimReferralInvite({
        company: companyIri,
        token,
      })
    },

    onSuccess: async () => {
      const singleReferral = referralInvites['hydra:member'][0]
      invariant(singleReferral, 'Expected at least 1 referral to be set')

      const redirectUri = hasMultipleReferrals
        ? ROUTES.referrals({ searchParams: { tab: REFERRALS_TAB.received } })
        : ROUTES.referralSummary({
            params: { referralId: singleReferral.id.toString() },
          })

      // If the user selected a different company, update the company context
      // and then redirect them to one of the referral pages
      if (hasSelectedDifferentCompany) {
        updateCompanyContext({ company: companyIri, redirectUri })
        return
      }

      notyf.success(
        `${hasMultipleReferrals ? 'Referrals' : 'Referral'} successfully claimed`,
      )

      await refreshSessionMetadata.mutateAsync()

      navigate(redirectUri)
    },

    onError: () =>
      notyf.error({
        type: 'error',
        message: `Sorry but there was a problem claiming ${referralsTerm} and our team has been notified. Please try again later.`,
        duration: LONG_MESSAGE_DURATION,
      }),
  })

  return (
    <ActionButton
      isProcessing={claimReferralMutation.isPending}
      onClick={() => claimReferralMutation.mutate()}
      className={cn('w-full sm:w-auto', className)}
      size="lg"
      {...props}
    >
      {children}
    </ActionButton>
  )
}

export default ClaimReferralButton
